import { useLocation } from "@/utils/location-provider";

import { DEFAULT_LOCALE, LOCALES } from "./localization.constants";
import { useLocale } from "./use-locale";

export function isLocalizableHref(href: string) {
    // only paths starting with a single slash to prevent localizing URLs
    // starts with a slash, followed by the end of the string or anything that's not a slash, excludes /blog/* urls
    return /^(?!\/\/)\/(blog$|$|(?!blog\/).+)/.test(href);
}

export function isAlreadyLocalizedHref(href: string) {
    return (
        LOCALES.some((locale) => href.startsWith(`/${locale}/`)) ||
        LOCALES.some((locale) => href === `/${locale}`)
    );
}

export interface UseLocalizedHrefOptions {
    supportedLocales?: string[];
}

function isWacPage(hostname: string): boolean {
    if (!hostname) return true;
    const lower = hostname.toLowerCase();
    return (
        lower === "www.atlassian.com" ||
        lower.startsWith("wac.") ||
        lower === "localhost" ||
        lower === "127.0.0.1"
    );
}

function getWacBaseUrl(hostname: string) {
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "http://localhost:9000";
    }
    if (hostname.includes("dev")) {
        return "https://wac.dev.internal.atlassian.com";
    }
    if (hostname.includes("stg")) {
        return "https://wac.stg.internal.atlassian.com";
    }
    return "https://www.atlassian.com";
}

/**
 * Hostname from the last useLocalizedHref (or useLocation) run in the tree.
 * Used by absoluteToRelativeHref so it can stay a plain function (no hooks).
 */
let currentHostname: string | undefined;

function getCurrentHostname(): string {
    if (currentHostname !== undefined) return currentHostname;
    if (typeof window !== "undefined") {
        return window.location?.hostname ?? "";
    }
    return "";
}

// Localized relative URL for WAC pages, full URL for non-WAC pages
export function useLocalizedHref(
    href: string,
    opts: UseLocalizedHrefOptions = {}
) {
    const { supportedLocales } = opts;
    const locale = useLocale();
    const localizedRelativePath = getLocalizedHref({
        locale,
        href,
        supportedLocales
    });

    const location = useLocation();
    currentHostname = location.hostname;
    const onWacPage = isWacPage(location.hostname);

    if (onWacPage) {
        return localizedRelativePath;
    } else {
        return relativeToAbsoluteHref(localizedRelativePath, location.hostname);
    }
}

export interface GetLocaledHrefParams {
    locale: string;
    href: string;
    supportedLocales?: string[];
}

export function getLocalizedHref({
    locale,
    href,
    supportedLocales
}: GetLocaledHrefParams) {
    if (
        locale === DEFAULT_LOCALE ||
        isAlreadyLocalizedHref(href) ||
        !isLocalizableHref(href)
    ) {
        return href;
    }

    if (supportedLocales) {
        return supportedLocales.includes(locale) ? `/${locale}${href}` : href;
    }

    return `/${locale}${href}`;
}

/**
 * Converts an absolute atlassian.com URL to a relative path when the current
 * page is a WAC page. Uses hostname from context (set by useLocalizedHref when
 * it runs in the tree) or window.location so this can stay a plain function.
 */
export function absoluteToRelativeHref(href: string): string {
    const baseUrl = /^https?:\/\/(www\.)?atlassian\.com/;
    const hostname = getCurrentHostname();
    const onWacPage = isWacPage(hostname);

    if (onWacPage && baseUrl.test(href)) {
        return href.replace(baseUrl, ""); // only change to relative URL when on WAC page
    }
    return href;
}

function relativeToAbsoluteHref(
    localizedHref: string,
    hostname: string
): string {
    const baseUrl = getWacBaseUrl(hostname);
    return `${baseUrl}${localizedHref}`;
}

function getTrelloPath(trelloRoute: string) {
    if (trelloRoute === "/") {
        return "";
    }
    return trelloRoute;
}

export function useLocalizedHrefTrello(trelloRoute: string) {
    const trelloBaseUrl = "https://trello.com";
    const locale = useLocale();
    const trelloPath = getTrelloPath(trelloRoute);
    if (locale === DEFAULT_LOCALE) {
        return `${trelloBaseUrl}${trelloPath}`;
    }
    return `${trelloBaseUrl}/${locale}${trelloPath}`;
}
